// Редактор смет: позиции (добавить/править/удалить), живой итог, выбор расценки.
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Save, BookOpen, Search, Calculator, X,
} from 'lucide-react';
import { estimateEditorApi, rateCatalogApi, getErrorMessage } from '@/api';
import { AppLayout } from '@/components/layout/AppLayout';
import { Spinner } from '@/components/ui/Spinner';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import { formatMoney } from '@/lib/format';
import type { EstimateLine, RateCatalogItem } from '@/types';

const STATUS_COLOR: Record<string, string> = {
  draft: 'var(--text-muted)', review: 'var(--yellow)', approved: 'var(--green)', rejected: 'var(--red)',
};

interface NewLine {
  code: string; name: string; unit: string; volume: string;
  unit_cost_total: string; unit_cost_labor: string; unit_cost_materials: string;
}
const EMPTY: NewLine = { code: '', name: '', unit: '', volume: '', unit_cost_total: '', unit_cost_labor: '', unit_cost_materials: '' };

export function EstimateEditorPage() {
  const { id } = useParams<{ id: string }>();
  const estimateId = Number(id);
  const navigate = useNavigate();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<NewLine>(EMPTY);
  const [rateOpen, setRateOpen] = useState(false);

  const { data: estimate, isLoading } = useQuery({
    queryKey: ['estimate-editor', estimateId],
    queryFn: () => estimateEditorApi.get(estimateId),
  });
  const { data: lines } = useQuery({
    queryKey: ['estimate-lines', estimateId],
    queryFn: () => estimateEditorApi.lines(estimateId),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['estimate-lines', estimateId] });
    qc.invalidateQueries({ queryKey: ['estimate-editor', estimateId] });
    qc.invalidateQueries({ queryKey: ['estimates'] });
  };

  const addM = useMutation({
    mutationFn: () => estimateEditorApi.addLine({
      estimate: estimateId,
      line_number: (lines?.length ?? 0) + 1,
      code: draft.code, name: draft.name, unit: draft.unit,
      volume: draft.volume || '0', unit_cost_total: draft.unit_cost_total || '0',
      unit_cost_labor: draft.unit_cost_labor || '0',
      unit_cost_materials: draft.unit_cost_materials || '0',
    }),
    onSuccess: () => { setDraft(EMPTY); invalidate(); toast('Позиция добавлена', 'success'); },
    onError: (e) => toast(getErrorMessage(e), 'error'),
  });
  const delM = useMutation({
    mutationFn: (lid: number) => estimateEditorApi.deleteLine(lid),
    onSuccess: () => { invalidate(); }, onError: (e) => toast(getErrorMessage(e), 'error'),
  });
  const updM = useMutation({
    mutationFn: ({ lid, payload }: { lid: number; payload: Partial<EstimateLine> }) => estimateEditorApi.updateLine(lid, payload),
    onSuccess: () => { invalidate(); }, onError: (e) => toast(getErrorMessage(e), 'error'),
  });

  const pickRate = (r: RateCatalogItem) => {
    setDraft({
      code: r.code, name: r.name, unit: r.unit, volume: draft.volume || '1',
      unit_cost_total: r.base_price, unit_cost_labor: r.price_labor, unit_cost_materials: r.price_materials,
    });
    setRateOpen(false);
  };

  const previewSum = (() => {
    const v = parseFloat(draft.volume || '0');
    const p = parseFloat(draft.unit_cost_total || '0');
    return v * p;
  })();

  if (isLoading) return <AppLayout><Spinner label="Загрузка сметы…" /></AppLayout>;
  if (!estimate) return <AppLayout><EmptyState title="Смета не найдена" /></AppLayout>;

  const readonly = estimate.status === 'approved';

  return (
    <AppLayout>
      <button onClick={() => navigate('/estimates')}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--text-secondary)', marginBottom: 16, cursor: 'pointer' }}>
        <ArrowLeft size={16} /> К сметам
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'var(--font-display)', fontSize: 26 }}>
            <Calculator size={22} style={{ color: 'var(--accent)' }} /> Смета {estimate.number}
          </h1>
          <div style={{ display: 'flex', gap: 14, marginTop: 6, alignItems: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
            <StatusBadge label={estimate.status_display} color={STATUS_COLOR[estimate.status]} />
            <span>Нормативная база: {estimate.norm_base?.toUpperCase()}</span>
            <span>Позиций: {lines?.length ?? 0}</span>
          </div>
        </div>
        <div className="panel" style={{ padding: '14px 20px', textAlign: 'right' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>ИТОГО по смете</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 24, color: 'var(--accent)' }}>{formatMoney(estimate.total)}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>без НДС · НДС {estimate.vat_percent}%</div>
        </div>
      </div>

      {/* Таблица позиций */}
      <div className="panel" style={{ padding: 0, overflow: 'auto' }}>
        <table className="data-table" style={{ minWidth: 900 }}>
          <thead>
            <tr>
              <th style={{ width: 40 }}>№</th>
              <th style={{ width: 130 }}>Шифр</th>
              <th>Наименование работ</th>
              <th style={{ width: 80 }}>Ед.</th>
              <th style={{ width: 90, textAlign: 'right' }}>Кол-во</th>
              <th style={{ width: 120, textAlign: 'right' }}>Цена, ₽</th>
              <th style={{ width: 130, textAlign: 'right' }}>Сумма, ₽</th>
              {!readonly && <th style={{ width: 40 }}></th>}
            </tr>
          </thead>
          <tbody>
            {(lines ?? []).map((ln) => (
              <EditableRow key={ln.id} line={ln} readonly={readonly}
                onSave={(payload) => updM.mutate({ lid: ln.id, payload })}
                onDelete={() => delM.mutate(ln.id)} />
            ))}
            {(!lines || lines.length === 0) && (
              <tr><td colSpan={readonly ? 7 : 8} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>
                Позиций пока нет — добавьте первую ниже
              </td></tr>
            )}

            {/* Строка добавления */}
            {!readonly && (
              <tr style={{ background: 'var(--bg-card)' }}>
                <td style={{ color: 'var(--text-muted)' }}>{(lines?.length ?? 0) + 1}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <input className="cell-input" value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} placeholder="Шифр" style={{ width: 80 }} />
                    <button title="Выбрать из справочника" onClick={() => setRateOpen(true)}
                      style={{ background: 'transparent', border: '1px solid var(--border-bright)', borderRadius: 4, color: 'var(--accent)', cursor: 'pointer', padding: '0 5px' }}>
                      <BookOpen size={13} />
                    </button>
                  </div>
                </td>
                <td><input className="cell-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Наименование работ" /></td>
                <td><input className="cell-input" value={draft.unit} onChange={(e) => setDraft({ ...draft, unit: e.target.value })} placeholder="ед." style={{ width: 70 }} /></td>
                <td><input className="cell-input" value={draft.volume} inputMode="decimal" onChange={(e) => setDraft({ ...draft, volume: e.target.value })} style={{ textAlign: 'right' }} /></td>
                <td><input className="cell-input" value={draft.unit_cost_total} inputMode="decimal" onChange={(e) => setDraft({ ...draft, unit_cost_total: e.target.value })} style={{ textAlign: 'right' }} /></td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' }}>{previewSum ? formatMoney(previewSum) : '—'}</td>
                <td>
                  <button title="Добавить" disabled={!draft.name || !draft.unit || addM.isPending} onClick={() => addM.mutate()}
                    style={{ background: 'var(--accent)', border: 'none', borderRadius: 4, color: '#1a1205', cursor: 'pointer', padding: '4px 6px', display: 'flex' }}>
                    <Plus size={15} />
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {readonly && (
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 12 }}>
          Смета утверждена — редактирование позиций недоступно.
        </p>
      )}

      <RatePickerModal open={rateOpen} onClose={() => setRateOpen(false)} onPick={pickRate} />
    </AppLayout>
  );
}

// Редактируемая строка существующей позиции
function EditableRow({ line, readonly, onSave, onDelete }: {
  line: EstimateLine; readonly: boolean; onSave: (p: Partial<EstimateLine>) => void; onDelete: () => void;
}) {
  const [edit, setEdit] = useState(false);
  const [vol, setVol] = useState(line.volume);
  const [price, setPrice] = useState(line.unit_cost_total);

  if (edit && !readonly) {
    return (
      <tr style={{ background: 'var(--accent-glow)' }}>
        <td>{line.line_number}</td>
        <td className="mono">{line.code}</td>
        <td>{line.name}</td>
        <td>{line.unit}</td>
        <td><input className="cell-input" value={vol} inputMode="decimal" onChange={(e) => setVol(e.target.value)} style={{ textAlign: 'right' }} /></td>
        <td><input className="cell-input" value={price} inputMode="decimal" onChange={(e) => setPrice(e.target.value)} style={{ textAlign: 'right' }} /></td>
        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
          {formatMoney(parseFloat(vol || '0') * parseFloat(price || '0'))}
        </td>
        <td>
          <div style={{ display: 'flex', gap: 4 }}>
            <button title="Сохранить" onClick={() => { onSave({ volume: vol, unit_cost_total: price }); setEdit(false); }}
              style={{ background: 'var(--green)', border: 'none', borderRadius: 4, color: '#fff', cursor: 'pointer', padding: '3px 5px', display: 'flex' }}><Save size={13} /></button>
            <button title="Отмена" onClick={() => { setVol(line.volume); setPrice(line.unit_cost_total); setEdit(false); }}
              style={{ background: 'transparent', border: '1px solid var(--border-bright)', borderRadius: 4, color: 'var(--text-muted)', cursor: 'pointer', padding: '3px 5px', display: 'flex' }}><X size={13} /></button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>{line.line_number}</td>
      <td className="mono">{line.code}</td>
      <td>{line.name}</td>
      <td>{line.unit}</td>
      <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{line.volume}</td>
      <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{formatMoney(line.unit_cost_total)}</td>
      <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 500 }}>{formatMoney(line.total_cost)}</td>
      {!readonly && (
        <td>
          <div style={{ display: 'flex', gap: 4 }}>
            <button title="Изменить" onClick={() => setEdit(true)}
              style={{ background: 'transparent', border: '1px solid var(--border-bright)', borderRadius: 4, color: 'var(--text-secondary)', cursor: 'pointer', padding: '3px 5px', display: 'flex', fontSize: 11 }}>✎</button>
            <button title="Удалить" onClick={onDelete}
              style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '3px', display: 'flex' }}><Trash2 size={13} /></button>
          </div>
        </td>
      )}
    </tr>
  );
}

// Модалка выбора расценки из справочника
function RatePickerModal({ open, onClose, onPick }: {
  open: boolean; onClose: () => void; onPick: (r: RateCatalogItem) => void;
}) {
  const [search, setSearch] = useState('');
  const [cat, setCat] = useState('');
  const { data: rates, isLoading } = useQuery({
    queryKey: ['rate-catalog', { search, cat }],
    queryFn: () => rateCatalogApi.list({ ...(search ? { search } : {}), ...(cat ? { category: cat } : {}) }),
    enabled: open,
  });

  return (
    <Modal open={open} onClose={onClose} title="Справочник расценок (ФЕР / ГЭСН)" width={680}
      footer={<Button onClick={onClose}>Закрыть</Button>}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input className="field__input" style={{ paddingLeft: 32 }} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Поиск: кровля, фасад, шифр…" />
        </div>
        <select className="field__select" style={{ maxWidth: 180 }} value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="">Все категории</option>
          <option value="roofing">Кровельные</option>
          <option value="facade">Фасадные</option>
          <option value="general">Общестрой</option>
          <option value="engineering">Инженерные</option>
          <option value="finishing">Отделочные</option>
        </select>
      </div>
      {isLoading ? <Spinner /> :
        !rates || rates.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 20 }}>
            Расценок не найдено. Наполните справочник командой <code>seed_rates</code> или через админку.
          </p>
        ) : (
          <div style={{ maxHeight: 400, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {rates.map((r) => (
              <button key={r.id} onClick={() => onPick(r)}
                style={{ textAlign: 'left', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 10, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--accent)' }}>{r.code}</div>
                  <div style={{ fontSize: 13, marginTop: 2 }}>{r.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{r.category_display} · {r.norm_base_display}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 14 }}>{formatMoney(r.base_price)}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>за {r.unit}</div>
                </div>
              </button>
            ))}
          </div>
        )}
    </Modal>
  );
}
